/**
 * HeroBuild — the isometric house that builds itself while the camera pulls out.
 *
 * A direct port of `hero-anim/hero-build.jsx` from the Claude Design project.
 * Every coordinate, dimension, colour, cue offset and easing choice below is
 * transcribed from that file; the only changes are the ones required to run it
 * as a typed React component in this app instead of on the design engine's
 * global-scope preview stage:
 *
 *   • `useComposition()` → the `t` prop, fed by `useCompositionClock`.
 *   • `window.{animate,interpolate,Easing,clamp}` → imports from `./timeline`.
 *   • `<CompositionStage width=1200 height=900>` → a 1200×900 absolutely
 *     positioned stage that the parent scales; the design embeds it at
 *     `hint-size="100%,100%"` inside a 4:3 box, which is the same thing.
 *   • The tweaks panel and `ScreenLabel` (preview-only tooling) are dropped.
 *     `showChips` survives as a prop because the design defaults it to true.
 *
 * The geometry: the world is `rotateX(60deg) rotateZ(45deg)`, so +z is up, the
 * `front` face shows lower-left and the `side` face lower-right. `Box` draws a
 * cuboid from a base centre at (x, y) elevated to z. Do not "simplify" the
 * transform chain — the faces are positioned by their own origins and the order
 * is load-bearing.
 *
 * Accessibility: the whole thing is decorative, so the stage is `aria-hidden`
 * and the hero's meaning lives entirely in the real heading and copy beside it.
 */
import {
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from "react";

import {
  AUTHORED_TOTAL,
  CUES,
  Easing,
  animate,
  clamp,
  interpolate,
} from "@/features/landing/hero/timeline";

/* ── MOTION helpers (verbatim) ─────────────────────────────────────────── */

const MOTION = {
  pop: (t: number, at: number, dur = 0.5): number =>
    animate({ from: 0, to: 1, start: at, end: at + dur, ease: Easing.easeOutBack })(t),
  rise: (t: number, at: number, dur = 0.6): number =>
    animate({ from: 0, to: 1, start: at, end: at + dur, ease: Easing.easeOutCubic })(t),
  fade: (t: number, at: number, dur = 0.4): number =>
    animate({ from: 0, to: 1, start: at, end: at + dur, ease: Easing.easeInOutSine })(t),
};

/* ── Box ───────────────────────────────────────────────────────────────── */

interface BoxProps {
  w: number;
  d: number;
  h: number;
  x?: number;
  y?: number;
  z?: number;
  /** Uniform scale. Below 0.005 the box is not rendered at all. */
  s?: number;
  /** Height scale — grows walls out of their plate. */
  hs?: number;
  o?: number;
  top: string;
  front: string;
  side: string;
  topChildren?: ReactNode;
  frontChildren?: ReactNode;
  sideChildren?: ReactNode;
}

function Box(p: BoxProps): ReactElement | null {
  const s = p.s == null ? 1 : p.s;
  const hs = p.hs == null ? 1 : p.hs;
  if (s < 0.005 || hs < 0.005) return null;
  const w = p.w;
  const d = p.d;
  const h = p.h * hs;
  const f: CSSProperties = { position: "absolute" };
  return (
    <div
      style={{
        position: "absolute",
        left: 0,
        top: 0,
        width: 0,
        height: 0,
        transformStyle: "preserve-3d",
        opacity: p.o == null ? 1 : p.o,
        transform: `translate3d(${p.x || 0}px, ${p.y || 0}px, 0) translateZ(${p.z || 0}px) scale3d(${s},${s},${s})`,
      }}
    >
      <div
        style={{
          ...f,
          width: w,
          height: d,
          left: -w / 2,
          top: -d / 2,
          background: p.top,
          transform: `translateZ(${h}px)`,
        }}
      >
        {p.topChildren}
      </div>
      <div
        style={{
          ...f,
          width: w,
          height: h,
          left: -w / 2,
          top: -h,
          transformOrigin: "50% 100%",
          transform: `translate3d(0, ${d / 2}px, 0) rotateX(-90deg)`,
          background: p.front,
          overflow: "hidden",
        }}
      >
        {p.frontChildren}
      </div>
      <div
        style={{
          ...f,
          width: d,
          height: h,
          left: -d / 2,
          top: -h,
          transformOrigin: "50% 100%",
          transform: `rotateZ(-90deg) translate3d(0, ${w / 2}px, 0) rotateX(-90deg)`,
          background: p.side,
          overflow: "hidden",
        }}
      >
        {p.sideChildren}
      </div>
    </div>
  );
}

/* ── Win / Chip / Cloud ────────────────────────────────────────────────── */

function Win(p: {
  left: number;
  bottom?: number;
  w?: number;
  h?: number;
  s: number;
  glow?: number;
}): ReactElement | null {
  if (p.s < 0.005) return null;
  return (
    <div
      style={{
        position: "absolute",
        left: p.left,
        bottom: p.bottom == null ? 34 : p.bottom,
        width: p.w || 44,
        height: p.h || 46,
        background: "#CFE2F6",
        border: "3px solid #FBFBFC",
        boxShadow: "0 0 0 2px rgba(15,15,20,0.10)",
        transformOrigin: "50% 100%",
        transform: `scale(${p.s})`,
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          bottom: 0,
          left: "50%",
          width: 3,
          marginLeft: -1.5,
          background: "#FBFBFC",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: -3,
          top: -3,
          right: -3,
          bottom: -3,
          background: "#FFD9A0",
          opacity: p.glow || 0,
        }}
      />
    </div>
  );
}

function Chip(p: { x: number; y: number; s: number; label: string }): ReactElement | null {
  if (p.s < 0.005) return null;
  return (
    <div
      style={{
        position: "absolute",
        left: p.x,
        top: p.y,
        display: "flex",
        alignItems: "center",
        gap: 10,
        background: "#FFFFFF",
        border: "1px solid rgba(15,15,20,0.07)",
        borderRadius: 10,
        padding: "10px 14px",
        boxShadow: "0 6px 18px rgba(24,22,36,0.10)",
        transformOrigin: "12% 110%",
        transform: `scale(${p.s})`,
        opacity: clamp(p.s, 0, 1),
        whiteSpace: "nowrap",
      }}
    >
      <div style={{ width: 9, height: 9, background: "#C75A34", transform: "rotate(45deg)" }} />
      {/* The chips print prices and bazaar names («1 250 сом», «Дордой») in
          every language, so they take the world's text face, Onest, which
          has Cyrillic; the design's Schibsted did not, and they fell back to
          the OS. */}
      <div
        style={{
          font: "600 24px var(--gd-text)",
          color: "#17201D",
        }}
      >
        {p.label}
      </div>
    </div>
  );
}

function Cloud(p: { x: number; y: number; k?: number }): ReactElement {
  return (
    <div
      style={{
        position: "absolute",
        left: p.x,
        top: p.y,
        opacity: 0.92,
        transform: `scale(${p.k || 1})`,
      }}
    >
      <div style={{ position: "absolute", left: 0, top: 12, width: 96, height: 30, borderRadius: 9999, background: "#FFFFFF" }} />
      <div style={{ position: "absolute", left: 18, top: 0, width: 46, height: 34, borderRadius: 9999, background: "#FFFFFF" }} />
      <div style={{ position: "absolute", left: 50, top: 6, width: 36, height: 26, borderRadius: 9999, background: "#FFFFFF" }} />
    </div>
  );
}

/* ── The piece ─────────────────────────────────────────────────────────── */

export interface HeroBuildProps {
  /** Authored seconds from `useCompositionClock`. */
  t: number;
  /** The design's `TWEAK_DEFAULTS.showChips`, which defaults to true. */
  showChips?: boolean;
}

export function HeroBuild({ t: T, showChips = true }: HeroBuildProps): ReactElement {
  const C = CUES;
  const R = C.Reset;
  /** Reset sweep: 1 until R+dt, then quickly to 0. */
  const out = (dt: number, dur?: number): number => 1 - MOTION.rise(T, R + dt, dur || 0.3);

  const sSlab = MOTION.pop(T, 0.5, 0.55) * out(0.85, 0.3);
  const sPlate = MOTION.pop(T, 1.05, 0.5) * out(0.72, 0.28);
  const hs1 = MOTION.rise(T, C.Framing + 0.15, 0.75) * out(0.5, 0.35);

  const r2 = MOTION.rise(T, C.Framing + 1.0, 0.7);
  const e2 = out(0.38, 0.3);
  const z2 = (1 - r2) * 260 + (1 - e2) * 260;
  const o2 = MOTION.fade(T, C.Framing + 1.0, 0.3) * e2;

  const rRoof1 = MOTION.rise(T, C.Details + 0.15, 0.6);
  const eRoof1 = out(0.3, 0.28);
  const oRoof1 = MOTION.fade(T, C.Details + 0.15, 0.25) * eRoof1;
  const rRoof2 = MOTION.rise(T, C.Details + 0.4, 0.6);
  const eRoof2 = out(0.34, 0.28);
  const oRoof2 = MOTION.fade(T, C.Details + 0.4, 0.25) * eRoof2;

  const sDoor = MOTION.pop(T, C.Details + 0.6, 0.45) * out(0.18, 0.22);
  const sCan = MOTION.pop(T, C.Details + 0.8, 0.45) * out(0.24, 0.22);
  const wt = C.Details + 0.85;
  const sW = (i: number): number => MOTION.pop(T, wt + i * 0.14, 0.45) * out(0.16, 0.2);
  const glow = MOTION.fade(T, C.Hold + 0.35, 0.8) * out(0.05, 0.2);

  const bp = MOTION.fade(T, 0.1, 0.3) * (1 - MOTION.fade(T, C.Framing + 0.6, 0.5));

  const tree = (at: number, lag: number): number =>
    MOTION.pop(T, C.Landscaping + at + lag, 0.5) * out(0.05, 0.25);
  const sShrub1 = MOTION.pop(T, C.Landscaping + 0.6, 0.45) * out(0.02, 0.2);
  const sShrub2 = MOTION.pop(T, C.Landscaping + 0.75, 0.45) * out(0.02, 0.2);
  const sPath = (i: number): number =>
    MOTION.pop(T, C.Landscaping + 0.25 + i * 0.15, 0.4) * out(0.1, 0.2);

  const chipGone = 1 - MOTION.fade(T, R - 0.3, 0.3);
  const sC1 = MOTION.pop(T, C.Details + 1.15, 0.5) * chipGone;
  const sC2 = MOTION.pop(T, C.Details + 1.7, 0.5) * chipGone;
  const sC3 = MOTION.pop(T, C.Details + 2.25, 0.5) * chipGone;

  const zoomBase = interpolate(
    [0, C.Framing - 0.1, C.Details + 0.1, C.Landscaping + 0.6, R, R + 1.05, AUTHORED_TOTAL],
    [2.15, 2.15, 1.35, 1, 1, 2.15, 2.15],
    [
      Easing.linear,
      Easing.easeInOutCubic,
      Easing.easeInOutCubic,
      Easing.linear,
      Easing.easeInOutCubic,
      Easing.linear,
    ],
  )(T);
  const drift = 0.018 * Math.sin(Math.PI * clamp((T - C.Hold) / (R - C.Hold), 0, 1));
  const zoom = zoomBase * (1 + drift);

  const loopPh = (2 * Math.PI * T) / AUTHORED_TOTAL;
  const cl1x = 420 + 34 * Math.sin(loopPh);
  const cl2x = 940 + 46 * Math.sin(loopPh + 2.1);
  const cl3x = 1120 + 30 * Math.sin(loopPh + 4.2);
  const sunGlow = 0.4 + 0.12 * Math.sin(3 * loopPh);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        background: "linear-gradient(180deg, #F3ECDD 0%, #F5EFE3 46%, #F7F2E9 100%)",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: 150,
          top: 80,
          width: 96,
          height: 96,
          borderRadius: 9999,
          background: "#FFE3AD",
          boxShadow: `0 0 64px 26px rgba(255,222,155,${sunGlow})`,
        }}
      />
      <Cloud x={cl1x} y={150} k={1.1} />
      <Cloud x={cl2x} y={110} k={0.85} />
      <Cloud x={cl3x} y={230} k={0.7} />

      {/* camera */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          transformOrigin: "760px 585px",
          transform: `scale(${zoom})`,
        }}
      >
        {/* isometric world */}
        <div
          style={{
            position: "absolute",
            left: 760,
            top: 600,
            width: 0,
            height: 0,
            transformStyle: "preserve-3d",
            transform: "rotateX(60deg) rotateZ(45deg)",
          }}
        >
          <Box w={620} d={460} h={26} top="#A8CBA0" front="#D8C5A8" side="#C4AF90" />
          {bp > 0.004 && (
            <div
              style={{
                position: "absolute",
                left: -180,
                top: -135,
                width: 360,
                height: 270,
                transform: "translateZ(27px)",
                border: "3px dashed #4E8F7B",
                background: "rgba(22,69,63,0.05)",
                boxSizing: "border-box",
                opacity: bp,
              }}
            />
          )}
          <Box s={sSlab} w={330} d={240} h={16} z={26} top="#DFDFE4" front="#CCCCD3" side="#BBBBC3" />
          <Box s={sPlate} w={310} d={220} h={8} z={42} top="#F0F0F3" front="#DDDDE2" side="#CDCDD4" />
          {/* story 1 — walls grow out of the plate */}
          <Box
            hs={hs1}
            w={290}
            d={200}
            h={108}
            z={50}
            top="#EFEFF2"
            front="#F8F8FA"
            side="#E9E9EE"
            frontChildren={
              <>
                <Win left={28} s={sW(0)} glow={glow} />
                <Win left={93} s={sW(1)} glow={glow} />
                <div
                  style={{
                    position: "absolute",
                    left: 187,
                    bottom: 0,
                    width: 36,
                    height: 60,
                    background: "#C75A34",
                    borderRadius: "4px 4px 0 0",
                    transformOrigin: "50% 100%",
                    transform: `scale(${sDoor})`,
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      right: 6,
                      top: 27,
                      width: 5,
                      height: 5,
                      borderRadius: 9999,
                      background: "#F7F2E9",
                    }}
                  />
                </div>
              </>
            }
            sideChildren={
              <>
                <Win left={36} s={sW(3)} glow={glow} />
                <Win left={118} s={sW(4)} glow={glow} />
              </>
            }
          />
          {/* story 2 — cedar volume drops in */}
          <Box
            s={o2 < 0.005 ? 0 : 1}
            o={o2}
            w={190}
            d={210}
            h={92}
            x={-50}
            z={158 + z2}
            top="#EDD9B8"
            front="#E4C79E"
            side="#D3B285"
            frontChildren={<Win left={30} bottom={26} w={130} h={44} s={sW(2)} glow={glow} />}
            sideChildren={<Win left={24} bottom={26} w={52} h={44} s={sW(5)} glow={glow} />}
          />
          {/* roof slabs */}
          <Box
            s={oRoof1 < 0.005 ? 0 : 1}
            o={oRoof1}
            w={112}
            d={212}
            h={10}
            x={95}
            z={158 + (1 - rRoof1) * 220 + (1 - eRoof1) * 220}
            top="#4B4B54"
            front="#3C3C44"
            side="#33333A"
          />
          <Box
            s={oRoof2 < 0.005 ? 0 : 1}
            o={oRoof2}
            w={202}
            d={222}
            h={10}
            x={-50}
            z={250 + (1 - rRoof2) * 220 + (1 - eRoof2) * 220}
            top="#4B4B54"
            front="#3C3C44"
            side="#33333A"
          />
          {/* door canopy */}
          <Box s={sCan} w={64} d={30} h={6} x={60} y={115} z={116} top="#4B4B54" front="#3C3C44" side="#33333A" />
          {/* path */}
          <Box s={sPath(0)} w={46} d={34} h={5} x={60} y={140} z={26} top="#E4E4E8" front="#D2D2D8" side="#C4C4CB" />
          <Box s={sPath(1)} w={46} d={34} h={5} x={60} y={178} z={26} top="#E4E4E8" front="#D2D2D8" side="#C4C4CB" />
          <Box s={sPath(2)} w={46} d={34} h={5} x={60} y={216} z={26} top="#E4E4E8" front="#D2D2D8" side="#C4C4CB" />
          {/* trees (voxel style) */}
          <Box s={tree(0.15, 0)} w={16} d={16} h={30} x={-235} y={150} z={26} top="#B59677" front="#A08363" side="#8D7355" />
          <Box s={tree(0.15, 0.08)} w={58} d={58} h={48} x={-235} y={150} z={50} top="#9CC48F" front="#86AE7C" side="#74996B" />
          <Box s={tree(0.15, 0.16)} w={38} d={38} h={34} x={-235} y={150} z={98} top="#9CC48F" front="#86AE7C" side="#74996B" />
          <Box s={tree(0.4, 0)} w={16} d={16} h={30} x={215} y={-165} z={26} top="#B59677" front="#A08363" side="#8D7355" />
          <Box s={tree(0.4, 0.08)} w={58} d={58} h={48} x={215} y={-165} z={50} top="#9CC48F" front="#86AE7C" side="#74996B" />
          <Box s={tree(0.4, 0.16)} w={38} d={38} h={34} x={215} y={-165} z={98} top="#9CC48F" front="#86AE7C" side="#74996B" />
          {/* shrubs by the path */}
          <Box s={sShrub1} w={32} d={32} h={22} x={112} y={200} z={26} top="#9CC48F" front="#86AE7C" side="#74996B" />
          <Box s={sShrub2} w={32} d={32} h={22} x={8} y={200} z={26} top="#9CC48F" front="#86AE7C" side="#74996B" />
        </div>
        {/* AI material callouts */}
        {showChips && (
          <>
            <Chip x={810} y={425} s={sC1} label="oak laminate · 1 250 сом/m²" />
            <Chip x={930} y={505} s={sC2} label="PVC windows · Дордой" />
            <Chip x={880} y={290} s={sC3} label="metal roofing · in stock" />
          </>
        )}
      </div>
    </div>
  );
}

/** The authored canvas, from `<CompositionStage width="1200" height="900">`. */
const STAGE_W = 1200;
const STAGE_H = 900;

/**
 * The stage. The design mounts `<CompositionStage width="1200" height="900">`
 * and lets the embed box scale it to `100%,100%`; here the same 1200×900 plane
 * is scaled to its container's width.
 *
 * The scale is measured with a ResizeObserver rather than expressed in CSS.
 * The obvious pure-CSS form — `scale(calc(100cqw / 1200))` — is INVALID: CSS
 * `calc()` cannot divide a length by a length to produce the bare number
 * `scale()` requires, so the whole `transform` is dropped as malformed and the
 * plane silently renders unscaled AND un-centred. (Measured: the world origin
 * landed at x=1571,y=1197 — entirely off-screen — with `transform: none`.)
 * Container queries only give you lengths; this needs a ratio.
 */
export function HeroBuildStage({
  t,
  showChips = true,
}: HeroBuildProps): ReactElement {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const measure = (): void => {
      const w = el.clientWidth;
      if (w > 0) setScale(w / STAGE_W);
    };
    measure();
    if (typeof ResizeObserver !== "function") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={hostRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        background: "#F3ECDD",
      }}
    >
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          width: STAGE_W,
          height: STAGE_H,
          transform: `translate(-50%, -50%) scale(${scale})`,
          transformOrigin: "50% 50%",
        }}
      >
        <HeroBuild t={t} showChips={showChips} />
      </div>
    </div>
  );
}

export default HeroBuild;
